// react
import * as React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// material ui
import { Avatar, IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import { DataGridPremium } from '@mui/x-data-grid-premium';

// feature-hooks
import useEnergyGroupUnit from '@/features/energyxplorer/hooks/useEnergyGroupType';

// components
import { useDatagridLocaleText } from '@/hooks/useDatagridLocaleText';
import { formatNumber } from '@/utils/formatters';

// relative
import {
  getTranslatedNodeType,
  getTypeColor,
  getTypeIcon,
} from '../../../../utils/nodeTypeMapper';
import StandardAnalysisToolbar from './StandardAnalysisToolbar';

const displayTime = (time) => {
  if (!time) {
    return '';
  }
  const hours = Math.floor(time);
  let minutes = Math.round((time % 1) * 60);
  if (minutes < 10) {
    minutes = `0${minutes}`;
  }
  return `${hours}:${minutes}`;
};

const formatTimeRange = (start, end) => {
  const startTime = displayTime(start);
  const endTime = displayTime(end);
  return { startTime, endTime };
};

const formatDays = (daysOfWeek, isActive) => {
  const dayNames = {
    monday: 'Mandag',
    tuesday: 'Tirsdag',
    wednesday: 'Onsdag',
    thursday: 'Torsdag',
    friday: 'Fredag',
    saturday: 'Lørdag',
    sunday: 'Søndag',
  };
  return (
    Object.entries(daysOfWeek)
      // eslint-disable-next-line no-unused-vars
      .filter(([_, active]) => active === isActive)
      .map(([day]) => dayNames[day.toLowerCase()] || day)
      .join(', ')
  );
};

export default function StandardAnalysisDatagrid(props) {
  const {
    initialRows = [],
    isLoading = true,
    selectedEnergyGroup = {},
  } = props;
  const [showPercentage, setShowPercentage] = React.useState(false);
  const { t } = useTranslation();
  const localeText = useDatagridLocaleText();

  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({});
  const { unit } = useEnergyGroupUnit();

  const visibilityModels = React.useMemo(() => {
    const percentageModel = {
      Name: true,
      Type: true,
      TotalEnergyUsage: false,
      Share: true,
      ActivePeriod: false,
      ActivePeriodPercentage: true,
      InactivePeriod: false,
      InactivePeriodPercentage: true,
      ActiveDays: false,
      ActiveDaysPercentage: true,
      InactiveDays: false,
      InactiveDaysPercentage: true,
    };

    const consumptionModel = {
      Name: true,
      Type: true,
      TotalEnergyUsage: true,
      Share: false,
      ActivePeriod: true,
      ActivePeriodPercentage: false,
      InactivePeriod: true,
      InactivePeriodPercentage: false,
      ActiveDays: true,
      ActiveDaysPercentage: false,
      InactiveDays: true,
      InactiveDaysPercentage: false,
    };

    return showPercentage ? percentageModel : consumptionModel;
  }, [showPercentage]);

  React.useEffect(() => {
    setColumnVisibilityModel(visibilityModels);
  }, [visibilityModels]);

  const activeTime = React.useMemo(() => {
    if (
      !selectedEnergyGroup?.daysOfWeek ||
      !selectedEnergyGroup.workingDayStart ||
      !selectedEnergyGroup.workingDayEnd
    )
      return '';
    const { startTime, endTime } = formatTimeRange(
      selectedEnergyGroup.workingDayStart,
      selectedEnergyGroup.workingDayEnd,
    );
    const days = formatDays(selectedEnergyGroup.daysOfWeek, true);

    return `Aktive timer er ${startTime}-${endTime}, på dagene: ${days}, dette kan ændres i opsætning.`;
  }, [
    selectedEnergyGroup.daysOfWeek,
    selectedEnergyGroup.workingDayEnd,
    selectedEnergyGroup.workingDayStart,
  ]);

  const inactiveTime = React.useMemo(() => {
    if (
      !selectedEnergyGroup?.daysOfWeek ||
      !selectedEnergyGroup.workingDayStart ||
      !selectedEnergyGroup.workingDayEnd
    )
      return '';
    const { startTime, endTime } = formatTimeRange(
      selectedEnergyGroup.workingDayStart,
      selectedEnergyGroup.workingDayEnd,
    );
    const days = formatDays(selectedEnergyGroup.daysOfWeek, true);
    const inactiveDays = formatDays(selectedEnergyGroup.daysOfWeek, false);

    return `Inaktive timer er 00:00-${startTime} og ${endTime}-23:59, på dagene: ${days}, og hele ${inactiveDays} dette kan ændres i opsætning.`;
  }, [
    selectedEnergyGroup.daysOfWeek,
    selectedEnergyGroup.workingDayEnd,
    selectedEnergyGroup.workingDayStart,
  ]);

  const activeDays = React.useMemo(() => {
    if (!selectedEnergyGroup?.daysOfWeek) return '';
    const days = formatDays(selectedEnergyGroup.daysOfWeek, true);
    return `Aktive dage: ${days}, dette kan ændres i opsætning.`;
  }, [selectedEnergyGroup]);

  const inactiveDays = React.useMemo(() => {
    if (!selectedEnergyGroup?.daysOfWeek) return '';
    const days = formatDays(selectedEnergyGroup.daysOfWeek, false);
    return `Inaktive dage: ${days}, dette kan ændres i opsætning.`;
  }, [selectedEnergyGroup]);

  const initialState = {
    sorting: {
      sortModel: [
        {
          field: 'TotalEnergyUsage',
          sort: 'desc',
        },
      ],
    },
  };

  const columns = [
    {
      field: 'Type',
      headerName: 'Type',
      valueFormatter: (value) => {
        return getTranslatedNodeType(value, t);
      },
    },
    {
      field: 'TotalEnergyUsage',
      headerName: 'Forbrug',
      type: 'number',
      valueFormatter: (value) => {
        return formatNumber(value) + ` ${unit}`;
      },
    },
    {
      field: 'Share',
      headerName: 'Andel',
      type: 'number',
      valueFormatter: (value) => {
        return formatNumber(value * 100) + '%';
      },
    },
    {
      field: 'ActivePeriod',
      headerName: 'Aktive timer',
      type: 'number',
      description: activeTime,
      valueFormatter: (value) => {
        return formatNumber(value) + ` ${unit}`;
      },
    },
    {
      field: 'ActivePeriodPercentage',
      headerName: 'Aktive timer',
      type: 'number',
      description: activeTime,
      valueFormatter: (value) => {
        return formatNumber(value) + '%';
      },
    },
    {
      field: 'InactivePeriod',
      headerName: 'Inaktive timer',
      type: 'number',
      description: inactiveTime,
      valueFormatter: (value) => {
        return formatNumber(value) + ` ${unit}`;
      },
    },
    {
      field: 'InactivePeriodPercentage',
      headerName: 'Inaktive timer',
      type: 'number',
      description: inactiveTime,
      valueFormatter: (value) => {
        return formatNumber(value * 100) + '%';
      },
    },
    {
      field: 'ActiveDays',
      headerName: 'Aktive dage',
      type: 'number',
      description: activeDays,
      valueFormatter: (value) => {
        return formatNumber(value) + ` ${unit}`;
      },
    },
    {
      field: 'ActiveDaysPercentage',
      headerName: 'Aktive dage',
      type: 'number',
      description: activeDays,
      valueFormatter: (value) => {
        return formatNumber(value * 100) + '%';
      },
    },
    {
      field: 'InactiveDays',
      headerName: 'Inaktive dage',
      type: 'number',
      description: inactiveDays,
      valueFormatter: (value) => {
        return formatNumber(value) + ` ${unit}`;
      },
    },
    {
      field: 'InactiveDaysPercentage',
      headerName: 'Inaktive dage',
      type: 'number',
      description: inactiveDays,
      valueFormatter: (value) => {
        return formatNumber(value * 100) + '%';
      },
    },
  ].map((column) => {
    return { ...column, flex: 1 };
  });

  return (
    <Box sx={{ height: '100%' }}>
      <DataGridPremium
        showToolbar
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        localeText={localeText}
        rows={initialRows}
        columns={columns}
        initialState={initialState}
        loading={isLoading}
        sortingOrder={['desc', 'asc', null]}
        treeData
        getTreeDataPath={(row) => row.hierarchy || [row.id]}
        defaultGroupingExpansionDepth={-1}
        groupingColDef={{
          headerName: 'Navn',
          width: 300,
          renderCell: (params) => {
            const nodeType = params.row?.Type;
            const nodeName = params.row?.Name;
            const depth = params.rowNode.depth;

            return (
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ ml: depth * 3 }}
              >
                {params.rowNode.type === 'group' && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      params.api.setRowChildrenExpansion(
                        params.id,
                        !params.rowNode.childrenExpanded,
                      );
                    }}
                    sx={{ p: 0.5 }}
                  >
                    {params.rowNode.childrenExpanded ? (
                      <ExpandMoreIcon fontSize="small" />
                    ) : (
                      <ChevronRightIcon fontSize="small" />
                    )}
                  </IconButton>
                )}
                <Avatar
                  variant="rounded"
                  sx={{
                    backgroundColor: getTypeColor(nodeType) || '#555',
                    color: '#fff',
                    fontWeight: 'bold',
                    borderRadius: 2,
                    width: 28,
                    height: 28,
                  }}
                >
                  <i
                    className={`fas ${getTypeIcon(nodeType)}`}
                    style={{ fontSize: '12px' }}
                  ></i>
                </Avatar>
                <span>{nodeName}</span>
              </Box>
            );
          },
        }}
        slots={{ toolbar: StandardAnalysisToolbar }}
        slotProps={{
          loadingOverlay: {
            variant: 'linear-progress',
            noRowsVariant: 'skeleton',
          },
          toolbar: {
            setShowPercentage: setShowPercentage,
            showPercentage: showPercentage,
          },
        }}
      />
    </Box>
  );
}
